import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "sidebar"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "arrow up" }, null, -1)
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = { class: "menu-links sidebar-ul" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 0,
  class: "fixed-arrow-up dropdown-menu-arrow"
}
const _hoisted_7 = {
  key: 1,
  class: "fixed-arrow-down dropdown-menu-arrow"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "blue-sidebar-decoration" }, null, -1)
const _hoisted_9 = { class: "sidebar-item-separation" }
const _hoisted_10 = { class: "main-sidebar-item" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "arrow down" }, null, -1)
const _hoisted_12 = [
  _hoisted_11
]

import { ref, Ref, inject, onMounted, onUnmounted, computed, nextTick } from "vue";
import { useRoute } from "vue-router";
import router from "@/router/index";
// Services
import { authService } from "@/auth/authSingleton";
import { AuthorisationLevel, GetAuthLevel } from "../config/AuthConfig";
// Misc
import log from "loglevel";
import { RoutePaths } from "@/router/RouteDetails";

interface ILink {
    name: keyof typeof RoutePaths;
    menuItems?: Array<keyof typeof RoutePaths>; 
    active?: boolean
}
interface IAuthLevel {
    name: keyof typeof RoutePaths;
    auth: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SideBar',
  setup(__props) {

const showComponent = inject("showComponent");
const route = useRoute();

const scrollAmount = ref(200);
const menuBar = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);

const scrollDown = (reverse: boolean = false) => {
    menuBar.value!.scrollBy({
        top: reverse ? -scrollAmount.value : scrollAmount.value,
        behavior: "smooth",
    });
};

const showScrollButtons = ref<boolean>(false);

onMounted(() => {
    log.trace("onMounted() SideBar");
    log.trace(`Showing links for:\nUser role: ${authService.getUsersRole()} (auth level: ${GetAuthLevel(authService.getUsersRole())})`);

    updateScrollButtons();

    window.addEventListener("resize", updateScrollButtons);
});

onUnmounted(() => {
    log.trace("onUnmounted() SideBar");
    window.removeEventListener("resize", updateScrollButtons);
});

function updateScrollButtons() {
    log.trace("updateScrollButtons() SideBar");
    if (!showComponent) return 
        nextTick(() => {
            log.trace("showComponent", showComponent);
            if (menu.value?.scrollHeight && menuBar.value?.clientHeight) {
                if (menu.value?.offsetHeight <= menuBar.value?.clientHeight) {
                    showScrollButtons.value = false;
                } else {
                    showScrollButtons.value = true;
                }
            }
        })
}

const links: Ref<ILink[]> = ref([
    { name: 'Home' },
    { name: 'Journaling' },
    { name: 'Alerts and Warnings' },
    { name: 'EoP Verifications' },
    { name: 'Invoices' },
    { name: 'Data Processing' },
    { name: 'Loan Management' },
    { name: 'Fund Management' },
    { name: 'Consumer Extract' },
    { name: 'Reports' },
    { name: 'Customer & Contract' },
    { name: 'Hire Agreements' },
    { name: 'Origination', 
        menuItems: [
            'Contract Validation', 
            'Contract Approval', 
            'Car Purchases'
        ], 
        active:false
    },
    { name: 'Payment Confirmation', 
        menuItems: [
            'Service Invoice Payments', 
            'Recharge Invoice Payments', 
            'Churn Payments', 'VAT Payments', 
            'Confirm Interest Payments', 
            'Final Settlement Payments',
            'Payments to VCFS'
        ],
        active:false
    },
    { name: 'Sage Backup' },
    { name: 'Fees Configuration' },
    { name: 'VAT Returns' },
    { name: 'Transfer Payments' },
    { name: 'Ledger Transactions' },
    { name: 'Funding Reports' },
    { name: 'Insurance Claims' },
    { name: 'Drawdown' },
    { name: 'VAT Loans' },
    { name: 'Portfolio Forecasts' },
    { name: 'VCFS Trial Balance' }
]);
function deactivateAllLinks(){
    links.value.forEach(link => {
        if(link != undefined){
            link.active = false
        }
    })
}
const routeAuthLevels: Ref<IAuthLevel[]> = ref(
    router.getRoutes().map((route) => {
        const authLevel = route.meta?.authLevel ?? AuthorisationLevel.NoAuthorisation;
        return { name: route.name, auth: authLevel } as IAuthLevel;
    })
);

function isSubPath(name: keyof typeof RoutePaths) {
    return route.fullPath.startsWith(RoutePaths[name].replace(/\/\:(\w)+\??/g,''));
}

function IsAuthorised(link: ILink): boolean {
    let authorisedSublinks = 0;
    let indexesToRemove: number[] = [];
    let authorised;

    const matchingRoute = routeAuthLevels.value.find(
        (route: IAuthLevel) => route.name === link.name
    );
    
    if(!link.menuItems){
        authorised = !matchingRoute?.auth || matchingRoute.auth <= GetAuthLevel(authService.getUsersRole());
    }
    if (link.menuItems) {
        for (const subLink of link.menuItems) {
            let matchingAuthRoute = routeAuthLevels.value.find( route => route.name === subLink)
            if (matchingAuthRoute) { 
                const sublinkAuthorised = matchingAuthRoute.auth  <= GetAuthLevel(authService.getUsersRole());
                if (sublinkAuthorised) {
                    authorisedSublinks += 1;
                } else {
                    const indexToRemove = link.menuItems.indexOf(subLink);
                    if (indexToRemove !== -1) {
                        indexesToRemove.push(indexToRemove);
                    }
                }
            }
        }
        authorised = authorisedSublinks !== 0;
        indexesToRemove.sort((a, b) => b - a);

        for (let index of indexesToRemove) {
            link.menuItems.splice(index, 1);
        }
    }

    log.trace(`IsAuthorised()\nlink: ${link.name} (requiring auth: ${matchingRoute?.auth})\nUser auth level: ${GetAuthLevel(authService.getUsersRole())}\nAuthorised: ${authorised}`);
    return authorised;
}

const authorisedLinks: Ref<ILink[]> = ref(
    links.value.filter((link: ILink) => IsAuthorised(link))
);
const routeName = computed(()=> route.name == "Car Purchase"? "Car Purchases": route.name )

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_unref(showComponent))
    ? (_openBlock(), _createElementBlock("nav", _hoisted_1, [
        (showScrollButtons.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (scrollDown(true))),
              id: "scroll-down",
              class: "scroll-up-button"
            }, _hoisted_3))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: _normalizeClass(["menu-bar", { 'with-scroll-buttons': showScrollButtons.value }]),
          ref_key: "menuBar",
          ref: menuBar
        }, [
          _createElementVNode("div", {
            class: "menu",
            ref_key: "menu",
            ref: menu
          }, [
            _createElementVNode("ul", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(authorisedLinks.value, (link, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: index,
                  class: _normalizeClass({
                        'sidebar-li':true,
                        'nav-link': true,
                        'current-page': routeName.value === link.name || isSubPath(link.name),
                        'sub-menu-open': link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName.value)),
                        'router-link-sub-menu' : link.menuItems
                    })
                }, [
                  (link.menuItems && link.menuItems.length > 0)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("span", {
                          onClick: () => { link.active = !link.active; updateScrollButtons(); },
                          class: _normalizeClass({
                                'router-link-sub-menu': true,
                                'sub-menu-active-decoration': (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName.value)))
                            })
                        }, [
                          _createTextVNode(_toDisplayString(link.name) + " ", 1),
                          (link.active || (link.menuItems && link.menuItems.some(item => item === routeName.value)))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                            : _createCommentVNode("", true),
                          (!link.active && ! (link.menuItems && link.menuItems.some(item => item === routeName.value)))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_5),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(link.menuItems, (subItem, subIndex) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: subIndex,
                            class: _normalizeClass({
                                'navlink': true,
                                'sub-link' : (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName.value))),
                                'invisible-sub-link': (link.active == false && !(link.menuItems && link.menuItems.some(item => item === routeName.value)) ) ,
                                'sub-menu-open-span': true,
                                'current-page-sub-menu': subItem === routeName.value, 
                                'last-sidebar-item': subIndex === link.menuItems.length - 1 && (link.active == true || (link.menuItems && link.menuItems.some(item => item === routeName.value)))
                            })
                          }, [
                            _hoisted_8,
                            _createVNode(_component_router_link, {
                              to: { name: subItem },
                              class: _normalizeClass({
                                    'nav-link': true,
                                    'router-link': true,
                                    'side-bar-decoration': true
                                })
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_9, _toDisplayString(subItem), 1)
                              ]),
                              _: 2
                            }, 1032, ["to"])
                          ], 2))
                        }), 128))
                      ], 64))
                    : (_openBlock(), _createBlock(_component_router_link, {
                        key: 1,
                        to: { name: link.name },
                        class: "router-link",
                        onClick: _cache[1] || (_cache[1] =  () => {deactivateAllLinks(); updateScrollButtons()} )
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_10, _toDisplayString(link.name), 1)
                        ]),
                        _: 2
                      }, 1032, ["to"]))
                ], 2))
              }), 128))
            ])
          ], 512)
        ], 2),
        (showScrollButtons.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 1,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (scrollDown())),
              id: "scroll-down",
              class: "scroll-down-button"
            }, _hoisted_12))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})